import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, UserPlus, Trash2, Search, Users } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";

const ROLES = ["owner", "admin", "editor", "viewer"];
const ROLE_TONE = {
  owner: "bg-primary/12 text-primary border-primary/30",
  admin: "bg-amber-500/15 text-amber-700 border-amber-500/40",
  editor: "bg-emerald-500/12 text-emerald-700 border-emerald-500/40",
  viewer: "bg-muted text-muted-foreground border-border",
};

function useDebounced(value, ms = 200) {
  const [v, setV] = useState(value);
  useEffect(() => { const id = setTimeout(() => setV(value), ms); return () => clearTimeout(id); }, [value, ms]);
  return v;
}

/**
 * AdminCompanyMembersPanel — CRUD for company_members with role selector
 * and typeahead search of registered users. Rendered inside the
 * Company detail Tabs as the "Members" tab.
 */
export function AdminCompanyMembersPanel({ companyId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("viewer");
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounced(query, 220);
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const containerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get(`/admin/companies/${companyId}/members`);
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load members");
    } finally {
      setLoading(false);
    }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  // Typeahead: never fires below 2 chars and abandons in-flight results
  // when the query changes so an earlier response can't overwrite the
  // list with stale rows.
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) { setCandidates([]); return; }
    let cancelled = false;
    setSearching(true);
    (async () => {
      try {
        const res = await client.get(`/admin/user-search?q=${encodeURIComponent(debouncedQuery)}&limit=8`);
        if (!cancelled) setCandidates(res.data?.items || []);
      } catch { if (!cancelled) setCandidates([]); }
      finally { if (!cancelled) setSearching(false); }
    })();
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  const addMember = async (candidate) => {
    setSaving(true);
    try {
      await client.post(`/admin/companies/${companyId}/members`, { user_id: candidate.user_id, role });
      toast.success(`Added ${candidate.email} as ${role}`);
      setQuery("");
      setCandidates([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add member");
    } finally {
      setSaving(false);
    }
  };

  const updateRole = async (membership_id, newRole) => {
    try {
      await client.patch(`/admin/companies/${companyId}/members/${membership_id}`, { role: newRole });
      setItems((xs) => xs.map((m) => (m.membership_id === membership_id ? { ...m, role: newRole } : m)));
      toast.success("Role updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update role");
    }
  };

  const removeMember = async (membership_id) => {
    if (!window.confirm("Remove this member from the company?")) return;
    try {
      await client.delete(`/admin/companies/${companyId}/members/${membership_id}`);
      setItems((xs) => xs.filter((m) => m.membership_id !== membership_id));
      toast.success("Member removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove member");
    }
  };

  // Members already assigned — used to grey out matching search candidates.
  const memberUserIds = new Set(items.map((m) => m.user_id));

  return (
    <div ref={containerRef} className="space-y-5" data-testid="admin-company-members-panel">
      <Card className="rounded-2xl border border-border p-4 sm:p-5 space-y-3">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <UserPlus className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="font-display font-semibold text-sm">Add member</h3>
            <p className="text-xs text-muted-foreground">
              Type an email or name — only registered users appear. Pick a role and click to add.
            </p>
          </div>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="rounded-lg h-9 w-[130px] text-xs" data-testid="admin-company-members-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => (
                <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email…"
            className="rounded-xl pl-9"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="admin-company-members-search"
          />
          {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
        {debouncedQuery.length >= 2 && (
          <div className="rounded-xl border border-border divide-y divide-border max-h-[260px] overflow-y-auto" data-testid="admin-company-members-results">
            {candidates.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No registered users match.</p>
            ) : candidates.map((c) => {
              const already = memberUserIds.has(c.user_id);
              return (
                <button
                  key={c.user_id}
                  type="button"
                  disabled={already || saving}
                  onClick={() => addMember(c)}
                  className={`w-full text-left px-3 py-2 hover:bg-muted/40 transition-colors flex items-center gap-3 ${already ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid={`admin-company-members-add-${c.user_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{c.name || c.email}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{c.email} · {c.role || "user"}</p>
                  </div>
                  {already ? (
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Already member</span>
                  ) : (
                    <UserPlus className="h-4 w-4 text-primary" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </Card>

      <Card className="rounded-2xl border border-border p-4 sm:p-5 space-y-3" data-testid="admin-company-members-list">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          <h3 className="font-display font-semibold text-sm">Members · {items.length}</h3>
        </div>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center" data-testid="admin-company-members-empty">
            No members yet. Add one above.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((m) => (
              <li
                key={m.membership_id}
                className="py-3 grid grid-cols-[1fr_auto_auto] items-center gap-3"
                data-testid={`admin-company-members-row-${m.user_id}`}
              >
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{m.user_name || m.user_email}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{m.user_email}</p>
                </div>
                <Select value={m.role} onValueChange={(v) => updateRole(m.membership_id, v)}>
                  <SelectTrigger
                    className={`h-8 rounded-lg text-xs w-[110px] capitalize ${ROLE_TONE[m.role] || ""}`}
                    data-testid={`admin-company-members-role-${m.user_id}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeMember(m.membership_id)}
                  className="text-muted-foreground hover:text-destructive"
                  aria-label="Remove member"
                  data-testid={`admin-company-members-remove-${m.user_id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

export default AdminCompanyMembersPanel;
